import React, { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, FileText } from 'lucide-react';
import AINetworkVisualization from './AINetworkVisualization';

function HeroDataStream() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {[...Array(5)].map((_, i) => (
        <div
          key={i}
          className="absolute top-0 w-px"
          style={{
            left: `${18 + i * 16}%`,
            height: '100%',
            background: `linear-gradient(180deg, transparent, rgba(0,212,255,${0.012 + i * 0.003}), transparent)`,
          }}
        >
          <div
            className="absolute w-full"
            style={{
              height: '80px',
              background: `linear-gradient(180deg, transparent, rgba(0,212,255,${0.04 + i * 0.006}), transparent)`,
              animation: `dataStream ${6 + i * 0.5}s linear infinite`,
              animationDelay: `${i * 1}s`,
            }}
          />
        </div>
      ))}
    </div>
  );
}

function LaunchCountdown() {
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    const target = new Date('2026-03-26T00:00:00Z');

    function tick() {
      const diff = target - Date.now();
      if (diff <= 0) {
        setTimeLeft({ launched: true });
        return;
      }
      setTimeLeft({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      });
    }

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  if (!timeLeft) return null;

  const pad = (n) => String(n).padStart(2, '0');

  return (
    <motion.a
      href="https://www.mexc.com/"
      target="_blank"
      rel="noopener noreferrer"
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="mb-4 inline-flex items-center gap-2.5 rounded-full border border-amber-400/30 bg-amber-400/8 px-4 py-1.5 backdrop-blur-md cursor-pointer hover:border-amber-400/50 transition-colors"
      data-testid="launch-banner"
    >
      <span className="relative flex h-1.5 w-1.5 shrink-0">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-70" style={{ animationDuration: '1.5s' }} />
        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-amber-400" />
      </span>
      <span className="font-mono text-[10px] tracking-wider text-amber-300/90">
        $RAX LAUNCHES ON MEXC — MARCH 26
      </span>
      {timeLeft.launched ? (
        <span className="font-mono text-[10px] font-bold text-amber-300">LIVE NOW</span>
      ) : (
        <span className="font-mono text-[10px] font-bold text-amber-300 tabular-nums">
          {timeLeft.days}d {pad(timeLeft.hours)}h {pad(timeLeft.minutes)}m {pad(timeLeft.seconds)}s
        </span>
      )}
    </motion.a>
  );
}

export default function HeroSection() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 30]);
  const opacityOut = useTransform(scrollYProgress, [0, 0.9], [1, 0]);

  return (
    <section
      ref={sectionRef}
      data-testid="hero-section"
      className="relative overflow-hidden pt-24 pb-0"
      style={{ maxWidth: '100vw' }}
    >
      {/* Background grid */}
      <motion.div className="pointer-events-none absolute inset-0 overflow-hidden" style={{ y: bgY }}>
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(0,212,255,0.02) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0,212,255,0.02) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
          }}
        />
        {/* Primary radial glow - centered, stronger */}
        <motion.div
          animate={{ scale: [1, 1.08, 1], opacity: [0.5, 0.7, 0.5] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute top-[20%] left-1/2 h-[500px] w-[min(700px,90vw)] -translate-x-1/2 -translate-y-1/2 rounded-full bg-rax-cyan/6 blur-[120px]"
        />
        {/* Secondary accent glow - creates depth */}
        <motion.div
          animate={{ scale: [1.05, 1, 1.05], opacity: [0.3, 0.45, 0.3] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
          className="absolute top-[30%] left-1/2 h-[350px] w-[min(450px,80vw)] -translate-x-1/2 -translate-y-1/2 rounded-full bg-rax-blue/5 blur-[80px]"
        />
        {/* Vignette edges for cinematic focus */}
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse 80% 60% at 50% 40%, transparent 0%, rgba(4,8,15,0.4) 100%)',
          }}
        />
      </motion.div>

      <HeroDataStream />

      {/* Main content */}
      <motion.div className="relative z-10 mx-auto max-w-7xl px-6" style={{ y: contentY, opacity: opacityOut }}>
        <div className="flex flex-col items-center text-center">
          {/* Token launch countdown */}
          <LaunchCountdown />

          {/* Protocol badge */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="mb-5 inline-flex items-center gap-2 rounded-full border border-rax-mint/25 bg-rax-mint/8 px-4 py-1.5 backdrop-blur-md"
            data-testid="hero-badge"
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rax-mint opacity-60" style={{ animationDuration: '2s' }} />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-rax-mint" />
            </span>
            <span className="font-mono text-[10px] tracking-wider text-rax-mint">
              LIVE ON BASE SEPOLIA TESTNET
            </span>
          </motion.div>

          {/* Main heading */}
          <div data-testid="hero-heading">
            <motion.h1
              className="max-w-4xl font-heading text-[2.25rem] font-800 leading-[1.1] tracking-tight sm:text-5xl md:text-[3.5rem] lg:text-[4rem]"
            >
              <motion.span
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="inline-block text-rax-heading"
              >
                The Risk Intelligence Layer
              </motion.span>
              <br />
              <motion.span
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.18, ease: [0.22, 1, 0.36, 1] }}
                className="inline-block"
              >
                <span className="bg-gradient-to-r from-rax-cyan via-rax-blue to-rax-cyan bg-[length:200%_auto] bg-clip-text text-transparent animate-shine">
                  DeFi Has Been Missing
                </span>
              </motion.span>
            </motion.h1>
          </div>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.26, ease: [0.22, 1, 0.36, 1] }}
            className="mt-4 max-w-xl font-body text-base leading-relaxed text-rax-body sm:text-lg"
            data-testid="hero-description"
          >
            Deposit capital, set your risk parameters, and let the AI engine continuously monitor, 
            rebalance, and optimize your DeFi portfolio.
          </motion.p>

          {/* CTA buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.34, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 flex flex-col items-center gap-3 sm:flex-row"
            data-testid="hero-ctas"
          >
            <a
              href="https://app.raxprotocol.xyz/"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative flex items-center gap-2 overflow-hidden rounded-lg bg-rax-cyan px-6 py-2.5 font-heading text-sm font-semibold text-rax-bg transition-all duration-300 hover:shadow-[0_0_28px_rgba(0,212,255,0.3)]"
              data-testid="hero-launch-app-btn"
            >
              <span className="relative z-10 flex items-center gap-2">
                Launch App
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5" />
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500" />
            </a>
            <a
              href="https://docs.raxprotocol.xyz/"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-2 rounded-lg border border-rax-border/60 bg-rax-card/30 px-6 py-2.5 font-heading text-sm font-semibold text-rax-heading backdrop-blur-md transition-all duration-300 hover:border-rax-border hover:bg-rax-card/50"
              data-testid="hero-docs-btn"
            >
              <FileText size={15} className="text-rax-body transition-colors duration-300 group-hover:text-rax-cyan" />
              Docs
            </a>
          </motion.div>

          {/* AI Network - the visual centerpiece */}
          <AINetworkVisualization />

          {/* Integrated chains - status layer between network and partners */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="flex items-center gap-3 mt-10 sm:mt-12 mb-16 sm:mb-20"
            data-testid="hero-chains"
          >
            <div className="flex items-center gap-2">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rax-mint opacity-50" style={{ animationDuration: '2s' }} />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-rax-mint" />
              </span>
              <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-rax-mint/70">
                Live on
              </span>
              <div className="flex h-6 w-6 items-center justify-center rounded-md border border-rax-mint/30 bg-rax-card/30 p-1">
                <img src="/base.png" alt="Base" className="h-4 w-4 object-contain" />
              </div>
            </div>
            <div className="h-4 w-px bg-rax-border/30" />
            <div className="flex items-center gap-2">
              <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-rax-body/40">
                Multi-chain intelligence
              </span>
              {[
                { src: '/ethereum.svg', alt: 'Ethereum' },
                { src: '/arbitrum.png', alt: 'Arbitrum' },
                { src: '/bnb.svg', alt: 'BNB Chain' },
                { src: '/hyperliquid.png', alt: 'Hyperliquid' },
              ].map((chain, i) => (
                <motion.div
                  key={chain.alt}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.9 + i * 0.04, duration: 0.3 }}
                  className="flex h-6 w-6 items-center justify-center rounded-md border border-rax-border/30 bg-rax-card/20 p-1"
                >
                  <img src={chain.src} alt={chain.alt} className="h-4 w-4 object-contain opacity-60" />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}
